'use client'

import { useState, useLayoutEffect, useEffect, useCallback, useContext } from 'react'
import { FdrPage } from 'utils/environmentConfig'
import { AnalyticsContext } from 'providers/AnalyticsProvider'
import { recordBannerEhConsent } from 'utils/consent/recordBannerEhConsent'
import { cookieStringHasConsentBanner } from 'constants/consentBannerCookies'

// useLayoutEffect fires synchronously before the browser paints, preventing a
// flash of the banner for returning visitors. It produces an SSR warning because
// Next.js pre-renders client components on the server — the isomorphic wrapper
// silences that without changing the client-side timing behavior.
const useSyncEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect

const COOKIE_CONFIG = {
  [FdrPage]: {
    name: 'tracking_consent_banner',
    prodDomain: '.freedomdebtrelief.com',
    devDomain: '.fdrgcp.com',
  },
  default: {
    name: 'achieve_tracking_consent_banner',
    prodDomain: '.achieve.com',
    devDomain: '.ffngcp.com',
  },
}

const BANNER_COPY_1 =
  'We use and allow cookies and similar tools on our websites to enable essential functionalities and to better understand how visitors use our site (including use of session replay technology), as well as for personalization, social media features, and advertising. Read our'
const BANNER_COPY_LINK = 'privacy policy'
const BANNER_COPY_3 =
  'to learn more. By closing this box or by your continued use of our sites, you accept such use.'

function getCookieDomain(siteEnv, appEnv) {
  if (!appEnv || appEnv === 'local') return undefined
  const config = siteEnv === FdrPage ? COOKIE_CONFIG[FdrPage] : COOKIE_CONFIG.default
  return appEnv === 'prd' ? config.prodDomain : config.devDomain
}

/**
 * ConsentBannerV2 — fully client-side consent banner.
 *
 * Starts with dismissed=false so the banner HTML is always present in the SSR
 * output (no hydration mismatch). After mount, useSyncEffect checks cookies /
 * session before paint and either hides the banner or removes the
 * consent-acknowledged class from <html> when Cloudflare served a cached page
 * from another user who had dismissed.
 */
const ConsentBannerV2 = function () {
  const {
    state: { analyticsInstance },
  } = useContext(AnalyticsContext)
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV
  const config = siteEnv === FdrPage ? COOKIE_CONFIG[FdrPage] : COOKIE_CONFIG.default

  // false = banner visible by default. Matches server render so there is no
  // hydration mismatch. useSyncEffect (useLayoutEffect on client) determines
  // the real state before the browser paints, preventing any visible flash.
  const [dismissed, setDismissed] = useState(false)

  useSyncEffect(() => {
    const hasCookie = cookieStringHasConsentBanner(document.cookie)
    const hasSession = sessionStorage.getItem('consent-banner-dismissed') === 'true'

    if (hasCookie || hasSession) {
      setDismissed(true)
    } else {
      // Cloudflare can cache a page that was rendered for a user who had the
      // consent cookie, which bakes class="consent-acknowledged" into the HTML.
      // Subsequent visitors without the cookie would then have the banner
      // hidden by CSS even though they never dismissed it. Removing the class
      // here corrects that cached-state mismatch.
      document.documentElement.classList.remove('consent-acknowledged')
    }
  }, [])

  const handleDismiss = useCallback(
    (event) => {
      const cookieDomain = getCookieDomain(siteEnv, appEnv)
      let cookieStr = `${config.name}=1; path=/; max-age=315360000; SameSite=Lax`
      if (cookieDomain) cookieStr += `; domain=${cookieDomain}`
      if (location.protocol === 'https:') cookieStr += '; Secure'
      document.cookie = cookieStr
      document.documentElement.classList.add('consent-acknowledged')
      sessionStorage.setItem('consent-banner-dismissed', 'true')
      const isClose = event?.currentTarget?.getAttribute?.('aria-label') === 'Close'
      recordBannerEhConsent(isClose ? 'banner_close' : 'banner_ok', analyticsInstance)
      setDismissed(true)
    },
    [siteEnv, appEnv, config.name, analyticsInstance]
  )

  if (dismissed) return null

  const cookieDomain = getCookieDomain(siteEnv, appEnv)
  const dismissAttrs = {
    'data-consent-dismiss': '',
    'data-cookie-name': config.name,
    ...(cookieDomain && { 'data-cookie-domain': cookieDomain }),
  }

  const wrapperStyle = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    width: '100%',
    zIndex: 50,
  }

  if (siteEnv === FdrPage) {
    return (
      <div
        className="consent-banner-fixed-wrap fixed bottom-0 left-0 right-0 w-full z-50"
        style={wrapperStyle}
      >
        <div
          className="consent-banner bg-neutral-0 shadow-[0_6px_40px_-4px_rgb(16_24_40/11%),0_9px_12px_-3px_rgb(16_24_40/5%)] lg:fixed flex max-w-[1440px] lg:max-w-[1200px] lg:-translate-x-2/4 w-full lg:left-2/4 bottom-0 lg:rounded-md decoration-[#4A4A4A] flex-col px-6 pb-3 lg:flex-row lg:pl-6 lg:pr-[45px] lg:py-4 lg:gap-3"
          data-testid="tcb-banner-static-1"
        >
          <div className="flex">
            <div className="bodyXs text-[#454545] pt-4 leading-[140%] lg:bodySm">
              {BANNER_COPY_1}&nbsp;
              <a
                href="/freedom-debt-relief-california-privacy-disclosures/"
                target="_blank"
                rel="noreferrer"
              >
                {BANNER_COPY_LINK}
              </a>
              &nbsp;{BANNER_COPY_3}
            </div>
            <button
              type="button"
              aria-label="Close"
              className="ml-2 p-1 cursor-pointer bg-transparent border-0"
              data-testid="tcb-close-container"
              {...dismissAttrs}
              onClick={handleDismiss}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/multi-site-assets/close-gray.svg"
                alt="Close button"
                width={24}
                height={24}
                aria-hidden
                data-testid="tcb-x-icon"
              />
            </button>
          </div>
          <div className="flex justify-center mt-3 lg:mt-0">
            <button
              type="button"
              className="bg-[#F0F3FF] w-[79px] h-[30px] cursor-pointer text-[13px] text-blue-900 font-bold border-0 rounded"
              data-testid="tcb-ok-button"
              {...dismissAttrs}
              onClick={handleDismiss}
            >
              Ok
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Achieve / Bills default markup
  return (
    <div
      className="consent-banner-fixed-wrap fixed bottom-0 left-0 right-0 w-full z-50"
      style={wrapperStyle}
    >
      <div
        className="consent-banner bg-neutral-0 shadow-[0_6px_40px_-4px_rgb(16_24_40/11%),0_9px_12px_-3px_rgb(16_24_40/5%)] lg:fixed flex max-w-[1440px] lg:max-w-[1200px] lg:-translate-x-2/4 w-full lg:left-2/4 bottom-0 lg:rounded-md decoration-[#4A4A4A]"
        data-testid="tcb-banner-static-2"
      >
        <div className="absolute right-3 top-3 lg:hidden" data-testid="tcb-close-container">
          <button
            type="button"
            aria-label="Close"
            className="bg-transparent border-0 p-0 cursor-pointer"
            {...dismissAttrs}
            onClick={handleDismiss}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/multi-site-assets/close.svg"
              alt="Close button"
              width={18}
              height={18}
              aria-hidden
              data-testid="tcb-x-icon"
            />
          </button>
        </div>

        <div className="bodyXs text-[#4A4A4A] text-justify pl-4 pr-11 lg:px-11 py-2 lg:py-8 w-full flex flex-col-reverse lg:block">
          <div className="flex justify-left lg:justify-center mt-2 lg:float-right lg:h-full lg:-mr-8 lg:flex lg:items-start lg:ml-4 lg:mt-0">
            <button
              type="button"
              aria-label="Close"
              className="lg:block hidden pt-4 pl-6 bg-transparent border-0 cursor-pointer"
              {...dismissAttrs}
              onClick={handleDismiss}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/multi-site-assets/close.svg"
                alt="Close button"
                width={18}
                height={18}
                aria-hidden
                data-testid="tcb-x-icon"
              />
            </button>

            <button
              type="button"
              className="px-6 bg-transparent border-0 cursor-pointer"
              {...dismissAttrs}
              onClick={handleDismiss}
            >
              <span className="inline-block p-2 bg-[#EFF5FF] text-[#42546B] md:text-[#4A4A4A] rounded">
                <span className="px-6 text-base md:text-lg font-bold">Ok</span>
              </span>
            </button>
          </div>

          <div className="my-2 text-[#4A4A4A] **:text-[#4A4A4A] **:decoration-[#4A4A4A]">
            {BANNER_COPY_1}&nbsp;
            <a href="/policies#Privacy-Policy" target="_blank" rel="noreferrer">
              {BANNER_COPY_LINK}
            </a>
            &nbsp;{BANNER_COPY_3}
          </div>
        </div>
      </div>
    </div>
  )
}

export { ConsentBannerV2 }
